import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDate } from '@/lib/utils';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { PageHeader } from '@/components/PageHeader';
import { StatusBadge } from '@/components/StatusBadge';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { CreditCard, Plus } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface BillingData {
  subscription?: {
    plan: string;
    status: string;
    currentPeriodEnd: number;
  };
  paymentMethods: Array<{
    id: string;
    type: string;
    last4: string;
    isDefault: boolean;
  }>;
  invoices: Array<{
    id: string;
    amount: number;
    status: string;
    date: number;
  }>;
}

interface BillingProps {
  user?: any;
}

function Billing({ user }: BillingProps) {
  const { t } = useTranslation();
  const [billing, setBilling] = useState<BillingData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isManageSubOpen, setIsManageSubOpen] = useState(false);
  const [isAddMethodOpen, setIsAddMethodOpen] = useState(false);
  const [isRemoveMethodOpen, setIsRemoveMethodOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('FREE');
  const [methodToRemove, setMethodToRemove] = useState<string | null>(null);
  const [newPaymentMethod, setNewPaymentMethod] = useState({
    cardNumber: '',
    expiryDate: '',
    cvv: '',
    nameOnCard: '',
  });

  useEffect(() => {
    fetchBilling();
  }, []);

  const fetchBilling = async () => {
    try {
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_URL || '';

      const response = await fetch(`${apiUrl}/api/billing`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setBilling(data.data);
      }
    } catch (error) {
      console.error('Failed to fetch billing:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const openManageSubscription = () => {
    if (billing?.subscription) {
      setSelectedPlan(billing.subscription.plan);
      setIsManageSubOpen(true);
    }
  };

  const handleManageSubscription = async () => {
    try {
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_URL || '';

      const response = await fetch(`${apiUrl}/api/billing/subscription`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ plan: selectedPlan }),
      });

      if (response.ok) {
        setIsManageSubOpen(false);
        fetchBilling();
      }
    } catch (error) {
      console.error('Failed to update subscription:', error);
    }
  };

  const handleAddPaymentMethod = async () => {
    try {
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_URL || '';

      const response = await fetch(`${apiUrl}/api/billing/payment-methods`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(newPaymentMethod),
      });

      if (response.ok) {
        setIsAddMethodOpen(false);
        setNewPaymentMethod({ cardNumber: '', expiryDate: '', cvv: '', nameOnCard: '' });
        fetchBilling();
      }
    } catch (error) {
      console.error('Failed to add payment method:', error);
    }
  };

  const openRemoveDialog = (methodId: string) => {
    setMethodToRemove(methodId);
    setIsRemoveMethodOpen(true);
  };

  const handleRemovePaymentMethod = async () => {
    if (!methodToRemove) return;

    try {
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_URL || '';

      const response = await fetch(`${apiUrl}/api/billing/payment-methods/${methodToRemove}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        setIsRemoveMethodOpen(false);
        setMethodToRemove(null);
        fetchBilling();
      }
    } catch (error) {
      console.error('Failed to remove payment method:', error);
    }
  };

  const handleDownloadInvoice = (invoice: { id: string; amount: number; date: number; status: string }) => {
    const invoiceData = `
Invoice #${invoice.id}
Amount: $${invoice.amount.toFixed(2)}
Date: ${formatDate(invoice.date)}
Status: ${invoice.status}
    `;

    const blob = new Blob([invoiceData], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `invoice-${invoice.id}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  if (isLoading) {
    return (
      <div className="w-full px-4 py-6 sm:px-6 lg:px-8">
        <Card>
          <CardContent className="pt-6">
            <p className="text-center text-muted-foreground">{t('common.loading')}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="w-full space-y-6 px-4 py-6 sm:px-6 lg:px-8">
      <PageHeader
        eyebrow="Billing"
        icon={CreditCard}
        title={t('billing.title')}
        subtitle="Billing and subscription management"
      />

      {billing?.subscription && (
        <Card>
          <CardHeader>
            <CardTitle>Current Subscription</CardTitle>
            <CardDescription>Your plan and billing information</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">{t('billing.plan')}</p>
                <div className="flex items-baseline gap-2">
                  <p className="text-2xl font-bold">{billing.subscription.plan}</p>
                  <span className="text-sm text-muted-foreground">
                    {billing.subscription.plan === 'FREE'
                      ? '(Up to 100 devices)'
                      : '(Pay as you go - $0.10/device/mo)'}
                  </span>
                </div>
              </div>
              <StatusBadge status={billing.subscription.status} />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Current Period Ends</p>
              <p className="font-medium">
                {formatDate(billing.subscription.currentPeriodEnd)}
              </p>
            </div>
            {['OWNER', 'ADMIN'].includes(user?.role) ? (
              <Button onClick={openManageSubscription}>{t('billing.subscription.manage')}</Button>
            ) : (
              <p className="text-sm text-muted-foreground mt-2 italic">
                {t('billing.subscription.onlyAdmin')}
              </p>
            )}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>{t('billing.paymentMethods.title')}</CardTitle>
              <CardDescription>
                {t('billing.paymentMethods.description', { count: billing?.paymentMethods?.length || 0 })}
              </CardDescription>
            </div>
            {['OWNER', 'ADMIN'].includes(user?.role) && (
              <Button variant="outline" onClick={() => setIsAddMethodOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                {t('billing.paymentMethods.add')}
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {billing?.paymentMethods && billing.paymentMethods.length > 0 ? (
            billing.paymentMethods.map((method) => (
              <div key={method.id} className="flex items-center justify-between p-4 border rounded-lg">
                <div className="flex items-center gap-4">
                  <CreditCard className="h-6 w-6" />
                  <div>
                    <p className="font-medium">
                      {method.type} ****{method.last4}
                    </p>
                    {method.isDefault && (
                      <Badge variant="outline" className="mt-1">
                        {t('billing.paymentMethods.default')}
                      </Badge>
                    )}
                  </div>
                </div>
                {['OWNER', 'ADMIN'].includes(user?.role) && (
                  <Button variant="ghost" size="sm" onClick={() => openRemoveDialog(method.id)}>
                    {t('billing.paymentMethods.remove')}
                  </Button>
                )}
              </div>
            ))
          ) : (
            <p className="text-center text-muted-foreground">{t('billing.paymentMethods.noMethods')}</p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('billing.invoices.title')}</CardTitle>
          <CardDescription>
            {t('billing.invoices.description', { count: billing?.invoices?.length || 0 })}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          {billing?.invoices && billing.invoices.length > 0 ? (
            billing.invoices.map((invoice) => (
              <div key={invoice.id} className="flex items-center justify-between p-4 border rounded-lg">
                <div>
                  <p className="font-medium">${invoice.amount.toFixed(2)}</p>
                  <p className="text-sm text-muted-foreground">
                    {formatDate(invoice.date)}
                  </p>
                </div>
                <div className="flex items-center gap-4">
                  <StatusBadge status={invoice.status} />
                  <Button variant="ghost" size="sm" onClick={() => handleDownloadInvoice(invoice)}>
                    {t('billing.invoices.download')}
                  </Button>
                </div>
              </div>
            ))
          ) : (
            <p className="text-center text-muted-foreground">{t('billing.invoices.noInvoices')}</p>
          )}
        </CardContent>
      </Card>

      {/* Manage Subscription Dialog */}
      <Dialog open={isManageSubOpen} onOpenChange={setIsManageSubOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('billing.dialogs.manageSubscription.title')}</DialogTitle>
            <DialogDescription>
              {t('billing.dialogs.manageSubscription.description')}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="plan">{t('billing.plan')}</Label>
              <Select value={selectedPlan} onValueChange={setSelectedPlan}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="FREE">Free (Up to 100 devices)</SelectItem>
                  <SelectItem value="PRO">Paid (Pay as you go - $0.10/device)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {billing?.subscription && (
              <div className="text-sm text-muted-foreground">
                <p>{t('billing.currentPlan')}: {billing.subscription.plan}</p>
                <p>{t('licenses.status')}: {billing.subscription.status}</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsManageSubOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handleManageSubscription}>
              {t('billing.upgradePlan')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Payment Method Dialog */}
      <Dialog open={isAddMethodOpen} onOpenChange={setIsAddMethodOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('billing.dialogs.addPaymentMethod.title')}</DialogTitle>
            <DialogDescription>
              {t('billing.dialogs.addPaymentMethod.description')}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="cardNumber">{t('billing.dialogs.addPaymentMethod.cardNumber')}</Label>
              <Input
                id="cardNumber"
                value={newPaymentMethod.cardNumber}
                onChange={(e) => setNewPaymentMethod({ ...newPaymentMethod, cardNumber: e.target.value })}
                placeholder="1234 5678 9012 3456"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="expiryDate">{t('billing.dialogs.addPaymentMethod.expiryDate')}</Label>
              <Input
                id="expiryDate"
                value={newPaymentMethod.expiryDate}
                onChange={(e) => setNewPaymentMethod({ ...newPaymentMethod, expiryDate: e.target.value })}
                placeholder="12/25"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="cvv">{t('billing.dialogs.addPaymentMethod.cvv')}</Label>
              <Input
                id="cvv"
                value={newPaymentMethod.cvv}
                onChange={(e) => setNewPaymentMethod({ ...newPaymentMethod, cvv: e.target.value })}
                placeholder="123"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="nameOnCard">{t('billing.dialogs.addPaymentMethod.nameOnCard')}</Label>
              <Input
                id="nameOnCard"
                value={newPaymentMethod.nameOnCard}
                onChange={(e) => setNewPaymentMethod({ ...newPaymentMethod, nameOnCard: e.target.value })}
                placeholder="John Doe"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAddMethodOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              onClick={handleAddPaymentMethod}
              disabled={
                !newPaymentMethod.cardNumber ||
                !newPaymentMethod.expiryDate ||
                !newPaymentMethod.cvv ||
                !newPaymentMethod.nameOnCard
              }
            >
              {t('billing.dialogs.addPaymentMethod.title')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Remove Payment Method Dialog */}
      <Dialog open={isRemoveMethodOpen} onOpenChange={setIsRemoveMethodOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('billing.dialogs.removePaymentMethod.title')}</DialogTitle>
            <DialogDescription>
              {t('billing.dialogs.removePaymentMethod.description')}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-muted-foreground">
              {t('billing.dialogs.removePaymentMethod.warning')}
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsRemoveMethodOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button variant="destructive" onClick={handleRemovePaymentMethod}>
              {t('billing.paymentMethods.remove')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default Billing;
